import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Grape, Wine, Coffee, Store, ShoppingBag, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { galleryApi } from '@/services/apiService';

const GALLERY_CONTENT = [
    {
        id: 1,
        title: 'Włoskie delikatesy',
        subtitle: 'prosto z Włoch',
        icon: ShoppingBag,
        fallbackImage: 'https://images.unsplash.com/photo-1578916171728-46686eac8d58?q=80&w=600&auto=format&fit=crop',
    },
    {
        id: 2,
        title: 'Wina i oliwy',
        subtitle: 'najlepszych producentów',
        icon: Wine,
        fallbackImage: 'https://images.unsplash.com/photo-1585553616435-2dc0a54e271d?q=80&w=600&auto=format&fit=crop',
    },
    {
        id: 3,
        title: 'Miejsce spotkań',
        subtitle: 'z włoskim klimatem',
        icon: Coffee,
        fallbackImage: 'https://images.unsplash.com/photo-1554118811-1e0d58224f24?q=80&w=600&auto=format&fit=crop',
    },
    {
        id: 4,
        title: 'Nasza Włoska Piwnica',
        subtitle: 'w sercu pasji i smaku',
        icon: Store,
        fallbackImage: 'https://images.unsplash.com/photo-1580922881198-43e93a54bba4?q=80&w=600&auto=format&fit=crop',
    }
];

const GallerySection = () => {
    const [images, setImages] = useState<string[]>([]);

    useEffect(() => {
        galleryApi.getGallery()
            .then(setImages)
            .catch(console.error);
    }, []);

    return (
        <section className="py-16 md:py-24 bg-background relative overflow-hidden" id="gallery">
            {/* Subtle line-art grape vines (left) */}
            <div className="absolute bottom-0 left-0 w-[400px] sm:w-[500px] h-[400px] pointer-events-none opacity-[0.07] z-0 overflow-hidden">
                <svg viewBox="0 0 800 800" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full text-primary" preserveAspectRatio="xMinYMax meet">
                    <g stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        {/* Main Branch */}
                        <path d="M -50 600 C 150 500 350 300 800 100" strokeWidth="4" />
                        <path d="M 0 620 C 200 520 400 320 850 120" strokeWidth="2" />
                        
                        {/* Curly Tendrils */}
                        <path d="M 300 420 C 100 200 -50 450 150 400 C 250 350 300 500 200 500 C 150 500 100 450 150 430" />
                        <path d="M 500 280 C 700 50 900 250 700 350 C 600 400 550 300 650 250 C 700 220 750 280 700 300" />
                        
                        {/* Leaf 1 (Top Left) */}
                        <g transform="translate(280, 220) rotate(-45) scale(2)">
                            <path d="M 0 0 L -15 -20 L -10 -5 L -40 -10 L -20 10 L -60 25 L -25 35 L -45 70 L -15 55 L -10 85 L 0 65 L 10 85 L 15 55 L 45 70 L 25 35 L 60 25 L 20 10 L 40 -10 L 10 -5 L 15 -20 Z" />
                            <path d="M 0 0 L 0 65 M 0 0 L -45 70 M 0 0 L 45 70 M 0 0 L -60 25 M 0 0 L 60 25" strokeWidth="1.5" />
                        </g>

                        {/* Leaf 2 (Right) */}
                        <g transform="translate(600, 350) rotate(15) scale(2.5)">
                            <path d="M 0 0 L -15 -20 L -10 -5 L -40 -10 L -20 10 L -60 25 L -25 35 L -45 70 L -15 55 L -10 85 L 0 65 L 10 85 L 15 55 L 45 70 L 25 35 L 60 25 L 20 10 L 40 -10 L 10 -5 L 15 -20 Z" />
                            <path d="M 0 0 L 0 65 M 0 0 L -45 70 M 0 0 L 45 70 M 0 0 L -60 25 M 0 0 L 60 25" strokeWidth="1.5" />
                        </g>

                        {/* Leaf 3 (Bottom) */}
                        <g transform="translate(380, 500) rotate(-100) scale(1.5)">
                            <path d="M 0 0 L -15 -20 L -10 -5 L -40 -10 L -20 10 L -60 25 L -25 35 L -45 70 L -15 55 L -10 85 L 0 65 L 10 85 L 15 55 L 45 70 L 25 35 L 60 25 L 20 10 L 40 -10 L 10 -5 L 15 -20 Z" />
                            <path d="M 0 0 L 0 65 M 0 0 L -45 70 M 0 0 L 45 70 M 0 0 L -60 25 M 0 0 L 60 25" strokeWidth="1.5" />
                        </g>

                        {/* Grapes Cluster (Bottom Center) */}
                        <g transform="translate(180, 480) scale(2.5)">
                            <circle cx="0" cy="0" r="15" />
                            <circle cx="28" cy="8" r="16" />
                            <circle cx="52" cy="0" r="14" />
                            <circle cx="-18" cy="20" r="16" />
                            <circle cx="12" cy="30" r="18" />
                            <circle cx="42" cy="32" r="16" />
                            <circle cx="-5" cy="50" r="17" />
                            <circle cx="25" cy="55" r="18" />
                            <circle cx="55" cy="52" r="15" />
                            <circle cx="10" cy="78" r="16" />
                            <circle cx="40" cy="75" r="15" />
                            <circle cx="25" cy="100" r="15" />
                            <circle cx="4" cy="105" r="14" />
                            <circle cx="18" cy="125" r="14" />
                        </g>
                    </g>
                </svg>
            </div>

            {/* Subtle line-art grape vines (right) */}
            <div className="absolute bottom-0 right-0 w-[400px] sm:w-[500px] h-[400px] pointer-events-none opacity-[0.07] z-0 overflow-hidden transform -scale-x-100">
                <svg viewBox="0 0 800 800" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full text-primary" preserveAspectRatio="xMinYMax meet">
                    <g stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        {/* Main Branch */}
                        <path d="M -50 600 C 150 500 350 300 800 100" strokeWidth="4" />
                        <path d="M 0 620 C 200 520 400 320 850 120" strokeWidth="2" />
                        
                        {/* Curly Tendrils */}
                        <path d="M 300 420 C 100 200 -50 450 150 400 C 250 350 300 500 200 500 C 150 500 100 450 150 430" />
                        <path d="M 500 280 C 700 50 900 250 700 350 C 600 400 550 300 650 250 C 700 220 750 280 700 300" />
                        
                        {/* Leaf 1 (Top Left) */}
                        <g transform="translate(280, 220) rotate(-45) scale(2)">
                            <path d="M 0 0 L -15 -20 L -10 -5 L -40 -10 L -20 10 L -60 25 L -25 35 L -45 70 L -15 55 L -10 85 L 0 65 L 10 85 L 15 55 L 45 70 L 25 35 L 60 25 L 20 10 L 40 -10 L 10 -5 L 15 -20 Z" />
                            <path d="M 0 0 L 0 65 M 0 0 L -45 70 M 0 0 L 45 70 M 0 0 L -60 25 M 0 0 L 60 25" strokeWidth="1.5" />
                        </g>

                        {/* Leaf 2 (Right) */}
                        <g transform="translate(600, 350) rotate(15) scale(2.5)">
                            <path d="M 0 0 L -15 -20 L -10 -5 L -40 -10 L -20 10 L -60 25 L -25 35 L -45 70 L -15 55 L -10 85 L 0 65 L 10 85 L 15 55 L 45 70 L 25 35 L 60 25 L 20 10 L 40 -10 L 10 -5 L 15 -20 Z" />
                            <path d="M 0 0 L 0 65 M 0 0 L -45 70 M 0 0 L 45 70 M 0 0 L -60 25 M 0 0 L 60 25" strokeWidth="1.5" />
                        </g>

                        {/* Grapes Cluster */}
                        <g transform="translate(200, 480) scale(2.5)">
                            <circle cx="0" cy="0" r="15" />
                            <circle cx="28" cy="8" r="16" />
                            <circle cx="-18" cy="20" r="16" />
                            <circle cx="12" cy="30" r="18" />
                            <circle cx="42" cy="32" r="16" />
                            <circle cx="-5" cy="50" r="17" />
                            <circle cx="25" cy="55" r="18" />
                            <circle cx="10" cy="78" r="16" />
                            <circle cx="40" cy="75" r="15" />
                            <circle cx="25" cy="100" r="15" />
                        </g>
                    </g>
                </svg>
            </div>

            <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8 max-w-[1400px]">
                {/* Header */}
                <div className="mb-14 flex flex-col items-center text-center">
                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="font-display text-4xl sm:text-5xl md:text-[56px] font-bold tracking-tight text-foreground"
                    >
                        Galeria
                    </motion.h2>

                    {/* Grape separator */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.1 }}
                        className="flex items-center gap-3 my-6"
                    >
                        <div className="h-[1.5px] w-16 bg-primary/30" />
                        <Grape className="w-5 h-5 text-primary/70" strokeWidth={1.5} />
                        <div className="h-[1.5px] w-16 bg-primary/30" />
                    </motion.div>

                    <motion.p
                        initial={{ opacity: 0, y: 15 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.2 }}
                        className="max-w-2xl text-[15px] sm:text-base text-muted-foreground leading-relaxed"
                    >
                        Zajrzyj do naszej Włoskiej Piwnicy – miejsca pełnego autentycznych smaków i pasji do włoskiej tradycji.
                    </motion.p>
                </div>

                {/* Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 xl:gap-8 mb-16">
                    {Array.from({ length: Math.max(images.length, GALLERY_CONTENT.length) }).map((_, index) => {
                        const itemTemplate = GALLERY_CONTENT[index % GALLERY_CONTENT.length];
                        const Icon = itemTemplate.icon;
                        const imageUrl = images[index] || itemTemplate.fallbackImage;
                        
                        return (
                            <motion.div
                                key={`gallery-item-${index}`}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-50px" }}
                                transition={{ delay: index * 0.1, duration: 0.5, ease: "easeOut" }}
                                className="group relative w-full h-[460px] rounded-[32px] overflow-hidden shadow-sm hover:shadow-xl transition-shadow duration-300 bg-muted"
                            >
                                {/* Image */}
                                <img
                                    src={imageUrl}
                                    alt={itemTemplate.title}
                                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                />
                                
                                {/* Inner Shadow/Gradient at the top for depth */}
                                <div className="absolute inset-0 bg-black/10 transition-opacity duration-300 group-hover:bg-black/0" />

                                {/* Bottom Info Gradient Overlay */}
                                <div className="absolute inset-x-0 bottom-0 h-[45%] bg-gradient-to-t from-[#f4ece1] via-[#f4ece1]/90 to-transparent pointer-events-none" />
                                
                                {/* Bottom Content */}
                                <div className="absolute inset-x-0 bottom-0 p-6 flex items-end gap-4">
                                    {/* Icon Box */}
                                    <div className="w-12 h-12 shrink-0 rounded-full bg-primary flex items-center justify-center text-white shadow-md">
                                        <Icon className="w-5 h-5" strokeWidth={1.5} />
                                    </div>
                                    
                                    {/* Text Info */}
                                    <div className="flex flex-col pb-1">
                                        <h3 className="text-[15px] font-bold text-foreground leading-tight mb-1">
                                            {itemTemplate.title}
                                        </h3>
                                        <p className="text-[13px] text-muted-foreground leading-tight">
                                            {itemTemplate.subtitle}
                                        </p>
                                    </div>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>

                {/* Call to action button */}
                <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.4 }}
                    className="flex justify-center"
                >
                    <Link
                        to="/produkty/wina"
                        className="inline-flex items-center gap-2 px-8 py-4 bg-primary text-primary-foreground rounded-full font-bold text-sm tracking-wider uppercase hover:bg-primary/90 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-primary/20 active:translate-y-0"
                    >
                        Zobacz nasz sklep
                        <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                </motion.div>
            </div>
        </section>
    );
};

export default GallerySection;
